import {
  ChoreWithDetails,
  HouseholdAggregate,
  ChoreAggregate,
  Chore,
  Activity,
  ChoreStatus,
  ScheduleKind,
  scheduleKind,
} from './chores';
import { calculateNextDueAfterCompletion, calculateNextDueFromSchedule } from './scheduleUtils';
import { isAssignedTo } from './members';

export type ChoreListItem = Chore & { nextDue: Date };

export function isChoreActive(choreAgg: ChoreAggregate): boolean {
  const hasBeenCompleted = choreAgg.activities.length > 0 || !!choreAgg.chore.last_completed_at;
  return !(choreAgg.chore.schedule.type === 'once' && hasBeenCompleted);
}

/** A label filter that keeps the chores with no labels at all. The NUL character keeps it from
 * ever being mistaken for a label someone typed. */
export const NO_LABEL_FILTER = '\0no-label';

/** Whose chores the dashboard lists: the user's own, or the whole household's. */
export type ChoreView = 'mine' | 'everyone';

/** The chores in the user's own view: those they're assigned to, primary or not, and those nobody
 * is, which anyone may take on. */
export function isMineOrUnassigned(chore: Pick<Chore, 'assigned_to'>, userId: string): boolean {
  return chore.assigned_to.length === 0 || isAssignedTo(chore, userId);
}

export function filterAndSortChores(
  items: ChoreListItem[],
  householdId: string,
  assigneeId: string,
  /** Keeps a chore that has any of these labels; empty keeps every chore. */
  labelFilter?: string[],
  searchText?: string,
  scheduleKindFilter?: ScheduleKind | ''
): ChoreListItem[] {
  const query = searchText?.trim().toLowerCase();

  return [...items]
    .sort((a, b) => a.nextDue.getTime() - b.nextDue.getTime())
    .filter(chore => {
      const matchesHousehold = !householdId || chore.household_id === householdId;
      const matchesAssignee = !assigneeId || chore.assigned_to.some(a => a.userId === assigneeId);
      const labels = chore.labels ?? [];
      const matchesLabel = !labelFilter?.length || labelFilter.some(wanted =>
        wanted === NO_LABEL_FILTER ? labels.length === 0 : labels.includes(wanted));
      const matchesSearch = !query ||
        chore.name.toLowerCase().includes(query) ||
        (chore.description ?? '').toLowerCase().includes(query) ||
        (chore.labels ?? []).some(l => l.toLowerCase().includes(query));
      const matchesSchedule = !scheduleKindFilter || scheduleKind(chore.schedule) === scheduleKindFilter;
      return matchesHousehold && matchesAssignee && matchesLabel && matchesSearch && matchesSchedule;
    });
}

export const mapHouseholdAggregateToChoresWithDetails = (agg: HouseholdAggregate) =>
    agg.chores
      .filter(isChoreActive)
      .map((choreAgg): ChoreWithDetails => {
        return mapHouseholdAggregateAndChoreAggregateToChoreWithDetails(agg, choreAgg);
      });

export const mapHouseholdAggregateAndChoreAggregateToChoreWithDetails = (agg: HouseholdAggregate, choreAgg: ChoreAggregate): ChoreWithDetails => {
    const { chore } = choreAgg;
    const { household } = agg;

    const lastActivity = getLastActivity(choreAgg);
    const baseDate = lastActivity ? new Date(lastActivity.completed_at) : new Date(chore.created_at);

    const nextDue = lastActivity
      ? calculateNextDueAfterCompletion(chore.schedule, baseDate)
      : calculateNextDueFromSchedule(chore.schedule, baseDate);
    const now = new Date();
    const diffInMs = nextDue.getTime() - now.getTime();
    const daysUntilDue = calendarDaysBetween(now, nextDue);
    const totalWindowMs = nextDue.getTime() - baseDate.getTime();
    const percentageUntilDue = totalWindowMs > 0 ? (diffInMs / totalWindowMs) * 100 : 0;

    return {
        ...chore,
        household,
        lastActivity,
        nextDue,
        status: calculateStatus(daysUntilDue),
        daysUntilDue,
        percentageUntilDue,
    };
};

// Whole calendar days from `from` to `to`, based on local midnight boundaries
// (e.g. 11pm today -> midnight tomorrow is 1 day, not ~0.04).
export function calendarDaysBetween(from: Date, to: Date): number {
  const startOfFrom = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const startOfTo = new Date(to.getFullYear(), to.getMonth(), to.getDate());
  return Math.round((startOfTo.getTime() - startOfFrom.getTime()) / (1000 * 60 * 60 * 24));
}

export function calculateNextDue(chore: Chore, lastActivity?: Activity): Date {
  return lastActivity
    ? calculateNextDueAfterCompletion(chore.schedule, new Date(lastActivity.completed_at))
    : calculateNextDueFromSchedule(chore.schedule, new Date(chore.created_at));
}

export function calculateStatus(daysUntilDue: number): ChoreStatus {
  if (daysUntilDue < 0) return ChoreStatus.Overdue;
  if (daysUntilDue === 0) return ChoreStatus.DueToday;
  return ChoreStatus.Upcoming;
}

/** Where a chore sits on the dashboard: overdue, due today, due in the next seven days, or later. */
export type Urgency = 'overdue' | 'today' | 'thisWeek' | 'later';

export const URGENCY_ORDER: Urgency[] = ['overdue', 'today', 'thisWeek', 'later'];

export const URGENCY_LABELS: Record<Urgency, string> = {
  overdue: 'Overdue',
  today: 'Today',
  thisWeek: 'This week',
  later: 'Later',
};

/** Counted in calendar days like the chore's status, so a chore in "Overdue" is always one whose
 * status is Overdue, and "Today" one whose status is DueToday. */
export function urgencyOf(nextDue: Date, now: Date = new Date()): Urgency {
  const days = calendarDaysBetween(now, nextDue);
  if (days < 0) return 'overdue';
  if (days === 0) return 'today';
  if (days <= 7) return 'thisWeek';
  return 'later';
}

/** Splits an already sorted list into the dashboard's groups, keeping the order within each. */
export function groupChoresByUrgency<T extends { nextDue: Date }>(items: T[], now: Date = new Date()): Record<Urgency, T[]> {
  const groups: Record<Urgency, T[]> = { overdue: [], today: [], thisWeek: [], later: [] };
  for (const item of items) groups[urgencyOf(item.nextDue, now)].push(item);
  return groups;
}

/** How many chores were last done within the past `days` calendar days, today included. Only a
 * chore's latest completion counts: that is what every device has, since the full history of
 * completions isn't synced. Includes one-off chores, which leave the list once done. With
 * `byUser`, only the chores that user did last. */
export function countChoresDoneWithin(choreAggs: ChoreAggregate[], days: number, now: Date = new Date(), byUser?: string): number {
  return choreAggs.filter(agg => {
    const last = getLastActivity(agg);
    if (!last || (byUser && last.by_user !== byUser)) return false;
    const ago = calendarDaysBetween(new Date(last.completed_at), now);
    return ago >= 0 && ago < days;
  }).length;
}

/** The two numbers on a household card. Counts the chores the dashboard lists, and "overdue" by
 * the same rule as the dashboard's Overdue group. */
export function summarizeHousehold(agg: HouseholdAggregate, now: Date = new Date()): { choreCount: number; overdueCount: number } {
  const active = agg.chores.filter(isChoreActive);
  const overdueCount = active.filter(
    c => urgencyOf(calculateNextDue(c.chore, getLastActivity(c)), now) === 'overdue'
  ).length;
  return { choreCount: active.length, overdueCount };
}

/** How a chore's due date reads in a list: "5 days overdue", "Today", "Tomorrow", "In 4 days",
 * and the date itself once it's two weeks or more away ("12 Mar", with the year when it isn't
 * this year's), since "In 180 days" says less than the date does. */
export function formatDueText(daysUntilDue: number, nextDue: Date, now: Date = new Date()): string {
  if (daysUntilDue < 0) {
    const late = -daysUntilDue;
    return `${late} ${late === 1 ? 'day' : 'days'} overdue`;
  }
  if (daysUntilDue === 0) return 'Today';
  if (daysUntilDue === 1) return 'Tomorrow';
  if (daysUntilDue < 14) return `In ${daysUntilDue} days`;
  return nextDue.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(nextDue.getFullYear() !== now.getFullYear() && { year: 'numeric' }),
  });
}

/** How far through its interval a chore is, from 0 (just done) to 1 (due), for the ring around
 * its done button. A chore that is due or overdue is always a full ring. */
export function elapsedFraction(status: ChoreStatus, percentageUntilDue: number): number {
  if (status !== ChoreStatus.Upcoming) return 1;
  return Math.max(0, Math.min(1, 1 - percentageUntilDue / 100));
}

export function getLastActivity(choreAgg: ChoreAggregate): Activity | undefined {
  const { chore, activities } = choreAgg;

  const localLast = activities.length > 0
    ? [...activities].sort((a, b) =>
        new Date(b.completed_at).getTime() - new Date(a.completed_at).getTime()
      )[0]
    : undefined;

  // Use chore's denormalized field when it is more recent than any local activity
  // (e.g. another household member completed the chore and it was synced in)
  if (chore.last_completed_at && chore.last_completed_by) {
    if (!localLast || new Date(chore.last_completed_at) > new Date(localLast.completed_at)) {
      return {
        id: `${chore.id}_last`,
        chore_id: chore.id,
        completed_at: chore.last_completed_at,
        by_user: chore.last_completed_by,
      };
    }
  }

  return localLast;
}
